import { createMuiTheme as createMUITheme } from '@material-ui/core/styles';
import { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';
import { ThemeProvider as ThemeStyledProvider } from 'styled-components';
import { ThemeProvider as ThemeMUIProvider } from '@material-ui/styles';

import { BASE_FONT_SIZE, BASE_LINE_HEIGHT } from './constants';
import { pxToRem } from './utils';
import { palette, Palette } from './palette';


type Typography = {
	baseFontSize: number;
	headline: TypographyElement;
	title: TypographyElement;
	title2: TypographyElement;
	subheading: TypographyElement;
	subheading2: TypographyElement;
	body: TypographyElement;
	body2: TypographyElement;
	caption: TypographyElement;
	caption2: TypographyElement;
	button: TypographyElement;
}

type TypographyElement = {
	fontSize: string;
	fontWeight: number;
	lineHeight: number;
	textTransform: string;
	styled: string;
}

export type CustomTheme = {
	typography: Typography;
	color: Palette;
	palette: Palette;
}

export type WithThemeProps = { theme: CustomTheme };
export type WithMaterialThemeProps =  ThemeOptions & { customTheme: CustomTheme };

function createDefaultTheme(): CustomTheme {
	const theme = {
		typography: {
			baseFontSize: BASE_FONT_SIZE,
			headline: {
				fontSize: pxToRem(BASE_FONT_SIZE + 10),
				fontWeight: 400,
				lineHeight: BASE_LINE_HEIGHT - 0.1,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE + 10)};
					font-weight: 400;
					line-height: ${BASE_LINE_HEIGHT - 0.1};
					text-transform: none;
				`,
			},
			title: {
				fontSize: pxToRem(BASE_FONT_SIZE + 6),
				fontWeight: 400,
				lineHeight: BASE_LINE_HEIGHT - 0.23,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE + 6)};
					font-weight: 400;
					line-height: ${BASE_LINE_HEIGHT - 0.23};
					text-transform: none;
				`,
			},
			title2: {
				fontSize: pxToRem(BASE_FONT_SIZE + 6),
				fontWeight: 500,
				lineHeight: BASE_LINE_HEIGHT - 0.23,
				textTransform: 'uppercase',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE + 6)};
					font-weight: 500;
					line-height: ${BASE_LINE_HEIGHT - 0.23};
					text-transform: uppercase;
				`,
			},
			subheading: {
				fontSize: pxToRem(BASE_FONT_SIZE + 4),
				fontWeight: 400,
				lineHeight: BASE_LINE_HEIGHT - 0.18,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE + 4)};
					font-weight: 400;
					line-height: ${BASE_LINE_HEIGHT - 0.18};
					text-transform: none;
				`,
			},
			subheading2: {
				fontSize: pxToRem(BASE_FONT_SIZE + 4),
				fontWeight: 500,
				lineHeight: BASE_LINE_HEIGHT - 0.18,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE + 4)};
					font-weight: 400;
					line-height: ${BASE_LINE_HEIGHT - 0.18};
					text-transform: none;
				`,
			},
			body: {
				fontSize: pxToRem(BASE_FONT_SIZE),
				fontWeight: 400,
				lineHeight: BASE_LINE_HEIGHT,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE)};
					font-weight: 400;
					line-height: ${BASE_LINE_HEIGHT};
					text-transform: none;
				`,
			},
			body2: {
				fontSize: pxToRem(BASE_FONT_SIZE),
				fontWeight: 500,
				lineHeight: BASE_LINE_HEIGHT,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE)};
					font-weight: 500;
					line-height: ${BASE_LINE_HEIGHT};
				`,
			},
			caption: {
				fontSize: pxToRem(BASE_FONT_SIZE - 2),
				fontWeight: 400,
				lineHeight: BASE_LINE_HEIGHT - 0.18,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE - 2)};
					font-weight: 400;
					line-height: ${BASE_LINE_HEIGHT - 0.18};
					text-transform: none;
				`,
			},
			caption2: {
				fontSize: pxToRem(BASE_FONT_SIZE - 2),
				fontWeight: 500,
				lineHeight: BASE_LINE_HEIGHT - 0.18,
				textTransform: 'none',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE - 2)};
					font-weight: 500;
					line-height: ${BASE_LINE_HEIGHT - 0.18};
					text-transform: none;
				`,
			},
			button: {
				fontSize: pxToRem(BASE_FONT_SIZE - 1),
				fontWeight: 500,
				lineHeight: 1,
				textTransform: 'uppercase',
				styled: `
					font-size: ${pxToRem(BASE_FONT_SIZE - 1)};
					font-weight: 500;
					line-height: ${BASE_LINE_HEIGHT - 0.28};
					text-transform: uppercase;
				`,
			}
		},
		color: palette,
		palette
	}

	return theme;
}

function createDefaultMaterialTheme() {
	const theme = createDefaultTheme();
	const { color } = theme;

	return createMUITheme({
		palette: {
			primary: {
				dark: color.accentPrimary,
				main: color.accentPrimary,
			},
			secondary: {
				dark: color.accentPrimary,
				main: color.accentPrimary,
			},
			text: {
				primary: color.blackPrimary,
				secondary: color.blackSecondary,
			},
			action: {
				disabledBackground: color.whiteStealthy,
				disabled: color.blackDisabled,
			},
			error: {
				contrastText: color.whitePrimary,
				main: color.dangerPrimary
			}
		},
		overrides: {
			MuiBackdrop: {
				root: {
					backgroundColor: 'rgba(62, 71, 87, 0.7)',
				}
			}
		},
		customTheme: theme
	} as ThemeOptions & { customTheme: CustomTheme });
}

const defaultTheme = createDefaultTheme();

const useCustomTheme = (props: any): [CustomTheme, boolean] => {
	const isStyled = Boolean(props && props.theme);
	const isMaterial = Boolean(props && props.customTheme);

	if (isStyled) {
		const isThemeActivated = Object.keys(props.theme).length > 0;
		const theme = isThemeActivated ? props.theme : defaultTheme;
		return [theme, isThemeActivated];
	}

	if (isMaterial) {
		const { customTheme } = props as WithMaterialThemeProps;
		const isThemeActivated = Boolean(Object.keys(customTheme).length > 0);
		const theme = isThemeActivated ? customTheme : defaultTheme;

		return [theme, isThemeActivated]; 
	}

	return [defaultTheme, false];
}


export {
	createDefaultTheme,
	createDefaultMaterialTheme,
	ThemeMUIProvider,
	ThemeStyledProvider,
	defaultTheme,
	useCustomTheme,
}